import { useEffect, useRef, useState } from "react";
import { loadSessions } from "./storage";
import type { SessionRecord } from "./types";

function initials(name: string) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function Particles() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d")!;
    let W: number, H: number;
    let dots: { x:number; y:number; r:number; vx:number; vy:number; a:number }[] = [];
    let raf: number;
    const init = () => {
      W = c.width = c.offsetWidth; H = c.height = c.offsetHeight;
      dots = Array.from({ length: 55 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        r: Math.random() * 1.4 + 0.4,
        vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
        a: Math.random() * 0.5 + 0.15,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      dots.forEach(d => {
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0) d.x = W; if (d.x > W) d.x = 0;
        if (d.y < 0) d.y = H; if (d.y > H) d.y = 0;
        ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,200,255,${d.a})`; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init); ro.observe(c);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);
  return <canvas ref={ref} style={{ position:"fixed",inset:0,width:"100%",height:"100%",pointerEvents:"none",zIndex:0 }} />;
}

function Spark({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const w = 80, h = 32;
  const min = Math.min(...data), max = Math.max(...data);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / (max - min || 1)) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} style={{ overflow:"visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      {data.map((v, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - ((v - min) / (max - min || 1)) * (h - 4) - 2;
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
      })}
    </svg>
  );
}

function Ring({ score, size = 56 }: { score: number; size?: number }) {
  const r = (size - 8) / 2, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 80 ? "#22c55e" : score >= 65 ? "#00c8ff" : "#f59e0b";
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
        fill="#f0f4ff" fontSize="12" fontFamily="Barlow Condensed" fontWeight="700">{score}</text>
    </svg>
  );
}

interface Props {
  onNewAnalysis: () => void;
  onCompare: () => void;
  onProfile: () => void;
  playerName?: string;
}

export default function Dashboard({ onNewAnalysis, onCompare, onProfile, playerName }: Props) {
  const [visible, setVisible] = useState(false);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);

  useEffect(() => {
    setTimeout(() => setVisible(true), 60);
    setSessions(loadSessions());
  }, []);

  const name = playerName || "Athlete";
  const ini = initials(name);

  // Derive all-time bests from real sessions
  const hasSessions = sessions.length > 0;
  const best = hasSessions ? {
    jumpHeight:   Math.max(...sessions.map(s => s.jumpHeight   || 0)),
    overall:      Math.max(...sessions.map(s => s.overall      || 0)),
    shots:        Math.max(...sessions.map(s => s.shots        || 0)),
    displacement: Math.max(...sessions.map(s => s.displacement || 0)),
  } : null;

  const BEST_CARDS = best ? [
    { label: "Jump Height",    value: `${best.jumpHeight} cm`,                     icon: "↑"  },
    { label: "Best Form",      value: `${best.overall} / 100`,                     icon: "✦"  },
    { label: "Most Shots",     value: `${best.shots}`,                             icon: "🏸" },
    { label: "Court Distance", value: `${(best.displacement/100).toFixed(1)} m`,   icon: "⚡" },
  ] : [];

  const latest2 = sessions.slice(0, 2);
  const s = latest2[0], p = latest2[1];

  type SK = keyof SessionRecord;
  const delta = (key: SK) => {
    if (!s || !p) return { val: 0, up: true };
    const d = ((s[key] as number) || 0) - ((p[key] as number) || 0);
    return { val: Math.abs(d), up: d >= 0 };
  };

  return (
    <div style={{ minHeight:"100vh", background:"#060b18", fontFamily:"Barlow, sans-serif",
      color:"#f0f4ff", position:"relative", overflowX:"hidden" }}>

      <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <Particles />

      <div style={{ position:"relative", zIndex:1, maxWidth:900, margin:"0 auto", padding:"0 20px 60px" }}>

        {/* Top Bar */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"22px 0 18px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
          <div>
            <div style={{ fontFamily:"Barlow Condensed", fontWeight:900, fontSize:28, letterSpacing:1,
              background:"linear-gradient(90deg,#00c8ff,#8b5cf6)", WebkitBackgroundClip:"text",
              WebkitTextFillColor:"transparent" }}>SHUTTLECOCK</div>
            <div style={{ fontSize:11, color:"#4a5568", fontWeight:600, letterSpacing:3, marginTop:-2, textTransform:"uppercase" }}>by statvo</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {/* Nav */}
            <button onClick={onProfile}
              style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8,
                color:"#a0aec0", padding:"8px 14px", fontSize:13, fontFamily:"Barlow, sans-serif", cursor:"pointer" }}>
              Profile
            </button>
            <button onClick={onCompare}
              style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8,
                color:"#a0aec0", padding:"8px 14px", fontSize:13, fontFamily:"Barlow, sans-serif", cursor:"pointer" }}>
              Compare
            </button>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{name}</div>
              <div style={{ fontSize:11, color:"#4a5568" }}>Athlete</div>
            </div>
            <div style={{ width:38, height:38, borderRadius:"50%", background:"linear-gradient(135deg,#00c8ff,#8b5cf6)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontFamily:"Barlow Condensed", fontWeight:900, fontSize:14, color:"#060b18" }}>
              {ini}
            </div>
          </div>
        </div>

        {/* New Analysis CTA */}
        <div style={{ opacity:visible?1:0, transform:visible?"translateY(0)":"translateY(16px)",
          transition:"all 0.5s ease", marginTop:28 }}>
          <button onClick={onNewAnalysis}
            style={{ width:"100%", padding:"18px 0", background:"#2563eb",
              border:"none", borderRadius:12, cursor:"pointer",
              fontFamily:"Barlow Condensed", fontWeight:800, fontSize:20, letterSpacing:2,
              color:"#fff", textTransform:"uppercase",
              boxShadow:"0 0 32px rgba(37,99,235,0.35)", transition:"background 0.2s, box-shadow 0.2s" }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#1d4ed8"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#2563eb"; }}>
            + New Analysis
          </button>
        </div>

        {/* Empty state */}
        {!hasSessions && (
          <div style={{ opacity:visible?1:0, transition:"opacity 0.5s", marginTop:48, textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🏸</div>
            <div style={{ fontFamily:"Barlow Condensed", fontWeight:800, fontSize:24, color:"#f0f4ff", marginBottom:8 }}>No sessions yet</div>
            <div style={{ fontSize:14, color:"#4a5568" }}>Record your first session to see stats, trends and AI coaching tips.</div>
          </div>
        )}

        {/* All-time Best */}
        {hasSessions && (
          <div style={{ opacity:visible?1:0, transition:"all 0.55s ease 0.1s", marginTop:28 }}>
            <div style={{ fontSize:11, fontWeight:600, letterSpacing:3, color:"#4a5568", marginBottom:12, textTransform:"uppercase" }}>All-Time Best</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
              {BEST_CARDS.map((b, i) => (
                <div key={i} style={{ background:"#0a1020", border:"1px solid rgba(255,255,255,0.07)",
                  borderRadius:10, padding:"14px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:20, marginBottom:4 }}>{b.icon}</div>
                  <div style={{ fontFamily:"Barlow Condensed", fontWeight:800, fontSize:18,
                    background:"linear-gradient(90deg,#00c8ff,#8b5cf6)", WebkitBackgroundClip:"text",
                    WebkitTextFillColor:"transparent" }}>{b.value}</div>
                  <div style={{ fontSize:10, color:"#4a5568", marginTop:3, fontWeight:500 }}>{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sessions */}
        {hasSessions && (
          <div style={{ opacity:visible?1:0, transition:"all 0.6s ease 0.18s", marginTop:28 }}>
            <div style={{ fontSize:11, fontWeight:600, letterSpacing:3, color:"#4a5568", marginBottom:12, textTransform:"uppercase" }}>Recent Sessions</div>
            <div style={{ display:"grid", gridTemplateColumns: latest2.length > 1 ? "1fr 1fr" : "1fr", gap:12 }}>
              {latest2.map((sess, i) => (
                <div key={sess.id} style={{ background:"#0a1020",
                  border: i===0 ? "1px solid rgba(0,200,255,0.25)" : "1px solid rgba(255,255,255,0.07)",
                  borderRadius:12, padding:"16px 16px 14px",
                  boxShadow: i===0 ? "0 0 20px rgba(0,200,255,0.07)" : "none" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <div style={{ fontFamily:"Barlow Condensed", fontWeight:800, fontSize:15,
                        color: i===0 ? "#00c8ff" : "#f0f4ff" }}>{sess.label}
                        {i===0 && <span style={{ marginLeft:6, fontSize:9, background:"rgba(0,200,255,0.15)",
                          color:"#00c8ff", padding:"2px 6px", borderRadius:4, fontWeight:600, letterSpacing:1 }}>LATEST</span>}
                      </div>
                      <div style={{ fontSize:11, color:"#4a5568", marginTop:2 }}>{sess.date} · {sess.duration}</div>
                    </div>
                    <Ring score={sess.overall} />
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                    {([ ["Smash",sess.smash],["Footwork",sess.footwork],["Landing",sess.landing],["Prep",sess.prep] ] as [string,number][]).map(([l,v]) => (
                      <div key={l} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                        background:"rgba(255,255,255,0.03)", borderRadius:6, padding:"5px 8px" }}>
                        <span style={{ fontSize:11, color:"#4a5568" }}>{l}</span>
                        <span style={{ fontSize:12, fontWeight:600, color: v>=80?"#22c55e":v>=65?"#00c8ff":"#f59e0b" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", gap:10, marginTop:10 }}>
                    <div style={{ flex:1, fontSize:11, color:"#4a5568" }}>Jump <span style={{ color:"#f0f4ff", fontWeight:600 }}>{sess.jumpHeight}cm</span></div>
                    <div style={{ flex:1, fontSize:11, color:"#4a5568" }}>Shots <span style={{ color:"#f0f4ff", fontWeight:600 }}>{sess.shots}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comparison Snapshot */}
        {s && p && (
          <div style={{ opacity:visible?1:0, transition:"all 0.65s ease 0.26s", marginTop:28 }}>
            <div style={{ fontSize:11, fontWeight:600, letterSpacing:3, color:"#4a5568", marginBottom:12, textTransform:"uppercase" }}>Session Comparison</div>
            <div style={{ background:"#0a1020", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:"16px" }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
                {([
                  { label:"Overall", key:"overall" as SK },
                  { label:"Jump",    key:"jumpHeight" as SK, unit:" cm" },
                  { label:"Shots",   key:"shots" as SK },
                  { label:"Smash",   key:"smash" as SK },
                ]).map(({ label, key, unit="" }) => {
                  const { val, up } = delta(key);
                  return (
                    <div key={key} style={{ textAlign:"center", padding:"10px 0" }}>
                      <div style={{ fontSize:11, color:"#4a5568", marginBottom:4 }}>{label}</div>
                      <div style={{ fontFamily:"Barlow Condensed", fontWeight:800, fontSize:22, color:"#f0f4ff" }}>{s[key] as number}{unit}</div>
                      <div style={{ fontSize:12, fontWeight:600, marginTop:3, color: up?"#22c55e":"#ef4444" }}>
                        {up?"▲":"▼"} {val}{unit}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Trend Charts */}
        {sessions.length >= 2 && (
          <div style={{ opacity:visible?1:0, transition:"all 0.7s ease 0.34s", marginTop:28 }}>
            <div style={{ fontSize:11, fontWeight:600, letterSpacing:3, color:"#4a5568", marginBottom:12, textTransform:"uppercase" }}>Progress</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {([
                { label:"Form Score",  data: sessions.map(s=>s.overall).reverse(), color:"#00c8ff", unit:"" },
                { label:"Jump Height", data: sessions.map(s=>s.jumpHeight).reverse(), color:"#8b5cf6", unit:" cm" },
              ]).map(({ label, data, color, unit }) => (
                <div key={label} style={{ background:"#0a1020", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:"14px 16px" }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:10, color:"#f0f4ff" }}>{label}</div>
                  <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between" }}>
                    <Spark data={data} color={color} />
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"Barlow Condensed", fontWeight:800, fontSize:22, color }}>{data[data.length-1]}{unit}</div>
                      <div style={{ fontSize:10, color:"#4a5568" }}>latest</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}